/**
 * Advanced Strategy Validation Service
 * 
 * Provides comprehensive validation for options strategy configurations including:
 * - Strike selection method validation
 * - Risk management configuration validation  
 * - Market timing validation
 * - Complex multi-leg strategy validation
 * - Indian market specialization rules
 */

import { FrontendStrategyData } from './strategyTransformationService';
import { Leg } from '../types/strategy';
import { getStrategyMtmInvalidReason } from '../utils/strategy/riskValidators';
import { legExposure } from '../utils/strategy/positionHelpers';
import { validatePercentageOfUnderlying } from '../utils/strategy/methodValidators';
import { MARKET_HOURS } from '../constants/tradingConstants';

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface LegValidationResult {
  legIndex: number;
  legId: string;
  errors: string[];
  warnings: string[];
}

class StrategyValidationService {
  /**
   * Comprehensive strategy validation
   */
  validateStrategy(strategyData: FrontendStrategyData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic field validation
    const basicValidation = this.validateBasicFields(strategyData);
    errors.push(...basicValidation.errors);
    warnings.push(...basicValidation.warnings);

    // Leg-specific validation
    const legValidation = this.validateLegs(strategyData.legs);
    legValidation.forEach(result => {
      result.errors.forEach(error => 
        errors.push(`Position ${result.legIndex + 1}: ${error}`)
      );
      result.warnings.forEach(warning => 
        warnings.push(`Position ${result.legIndex + 1}: ${warning}`)
      );
    });

    // Strategy-level configuration validation
    const configValidation = this.validateStrategyConfig(strategyData);
    errors.push(...configValidation.errors);
    warnings.push(...configValidation.warnings);

    // Advanced strategy validation
    const advancedValidation = this.validateAdvancedRules(strategyData);
    errors.push(...advancedValidation.errors);
    warnings.push(...advancedValidation.warnings);

    return {
      isValid: errors.length === 0,
      errors: Array.from(new Set(errors)), // Remove duplicates
      warnings: Array.from(new Set(warnings))
    };
  }

  /**
   * Validate basic required fields
   */
  private validateBasicFields(strategyData: FrontendStrategyData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!strategyData.strategyName?.trim()) {
      errors.push('Strategy name is required');
    } else if (strategyData.strategyName.length < 3) {
      warnings.push('Strategy name should be at least 3 characters long');
    }

    if (!strategyData.basketId?.trim()) {
      errors.push('Basket ID is required');
    }

    if (!strategyData.index) {
      errors.push('Index selection is required');
    } else if (!['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY', 'SENSEX', 'BANKEX'].includes(strategyData.index)) {
      errors.push('Invalid index selected');
    }

    if (!strategyData.legs || strategyData.legs.length === 0) {
      errors.push('At least one position is required');
    } else if (strategyData.legs.length > 6) {
      warnings.push('Strategies with more than 6 legs may have higher execution complexity');
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Validate individual legs/positions
   */
  private validateLegs(legs: Leg[]): LegValidationResult[] {
    return legs.map((leg, index) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      // Basic leg validation
      if (leg.totalLots <= 0) {
        errors.push('Total lots must be greater than 0');
      } else if (leg.totalLots > 200) {
        errors.push('Total lots cannot exceed 200');
      }

      // Strike selection method validation
      const strikeValidation = this.validateStrikeSelection(leg);
      errors.push(...strikeValidation.errors);
      warnings.push(...strikeValidation.warnings);

      // Risk management validation
      const riskValidation = this.validateRiskManagement(leg);
      errors.push(...riskValidation.errors);
      warnings.push(...riskValidation.warnings);

      return {
        legIndex: index,
        legId: leg.id,
        errors,
        warnings
      };
    });
  }

  /**
   * Validate strike selection methods and their configurations
   */
  private validateStrikeSelection(leg: Leg): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    switch (leg.selectionMethod) {
      case 'ATM_POINTS':
        if (!leg.strikePrice || leg.strikePrice.trim() === '') {
          errors.push('Strike selection value is required for ATM Points method');
        } else if (!this.isValidATMPointStrike(leg.strikePrice)) {
          errors.push('Invalid ATM Points strike price format (use ATM, OTM3, ITM5, etc.)');
        }
        break;

      case 'ATM_PERCENT':
        if (!leg.strikePrice || leg.strikePrice.trim() === '') {
          errors.push('Strike selection value is required for ATM Percent method');
        } else if (!this.isValidATMPercentStrike(leg.strikePrice)) {
          errors.push('Invalid ATM Percent strike price format (use ATM, ATM+5.00%, ATM-2.50%, etc.)');
        }
        break;

      case 'PREMIUM':
        // Validate premium criteria
        if (!leg.premiumOperator) {
          errors.push('Premium operator is required for Premium method');
        }
        if (leg.premiumValue === undefined || leg.premiumValue < 0) {
          errors.push('Premium value must be a positive number');
        } else if (leg.premiumValue > 1000) {
          warnings.push('Very high premium value may limit available strikes');
        }
        break;

      case 'PERCENTAGE_OF_STRADDLE_PREMIUM':
        // Validate straddle premium criteria
        if (!leg.straddlePremiumOperator) {
          errors.push('Premium operator is required for Percentage of Straddle Premium method');
        }
        if (leg.straddlePremiumPercentage === undefined || leg.straddlePremiumPercentage <= 0) {
          errors.push('Straddle premium percentage must be greater than 0');
        } else if (leg.straddlePremiumPercentage > 80) {
          warnings.push('High straddle premium percentage may limit available strikes');
        }
        break;

      case 'STRADDLE_WIDTH':
        // Signed multiplier of ATM straddle premium, range [-10, 10] in 0.25 steps
        if (leg.straddleWidth === undefined || leg.straddleWidth < -10 || leg.straddleWidth > 10) {
          errors.push('Straddle width must be between -10 and +10');
        }
        break;

      case 'PERCENTAGE_OF_UNDERLYING':
        errors.push(...validatePercentageOfUnderlying(leg));
        break;

      default:
        errors.push('Invalid strike selection method');
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Validate risk management configurations
   */
  private validateRiskManagement(leg: Leg): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Stop Loss validation (with defensive checks)
    // RANGE type uses rangeSide (HIGH/LOW) instead of a numeric value
    if (leg.stopLoss?.enabled && leg.stopLoss?.type !== 'RANGE') {
      if ((leg.stopLoss?.value || 0) <= 0) {
        errors.push('Stop Loss value must be greater than 0');
      } else if (leg.stopLoss?.type === 'POINTS' && (leg.stopLoss?.value || 0) > 500) {
        warnings.push('Very high Stop Loss points value may not trigger effectively');
      }
    }

    // Target Profit validation (with defensive checks)
    if (leg.targetProfit?.enabled) {
      if ((leg.targetProfit?.value || 0) <= 0) {
        errors.push('Target Profit value must be greater than 0');
      } else if (leg.targetProfit?.type === 'PERCENTAGE' && (leg.targetProfit?.value || 0) > 500) {
        warnings.push('Very high Target Profit percentage may rarely trigger');
      }
    }

    // Trailing Stop Loss validation (with defensive checks)
    if (leg.trailingStopLoss?.enabled) {
      if (!leg.stopLoss?.enabled) {
        errors.push('Trailing Stop Loss requires regular Stop Loss to be enabled');
      }
      if ((leg.trailingStopLoss?.instrumentMoveValue || 0) <= 0) {
        errors.push('Instrument move value must be greater than 0');
      }
      if ((leg.trailingStopLoss?.stopLossMoveValue || 0) <= 0) {
        errors.push('Stop Loss move value must be greater than 0');
      }
      if ((leg.trailingStopLoss?.instrumentMoveValue || 0) < (leg.trailingStopLoss?.stopLossMoveValue || 0)) {
        warnings.push('Stop Loss move value should typically be less than instrument move value');
      }
    }

    // Wait & Trade validation (with defensive checks)
    if (leg.waitAndTrade?.enabled && (leg.waitAndTrade?.value || 0) <= 0) {
      errors.push('Wait & Trade value must be greater than 0');
    }
    // Sell-side WAT with PERCENTAGE ≥ 100% is almost always a fat-finger
    // typo (200 meant as 20). Kept in lockstep with the utility validator
    // in strategyValidators.ts and the backend `validate_wat_config` guard
    // in libs/shared/shared/validators.py. All three must agree.
    if (leg.waitAndTrade?.enabled
        && leg.waitAndTrade?.type === 'PERCENTAGE'
        && leg.actionType === 'SELL'
        && (leg.waitAndTrade?.value || 0) >= 100) {
      errors.push('Sell-side Wait & trade percentage must be less than 100');
    }

    // Re-entry / Re-cost / Re-execute validation (with defensive checks)
    if (leg.reEntry?.enabled) {
      const reEntryCount = leg.reEntry?.count || 0;
      if (reEntryCount <= 0 || reEntryCount > 5) {
        errors.push('Re-entry count must be between 1 and 5');
      }
      if (!leg.stopLoss?.enabled) {
        errors.push('Re-entry / Re-cost / Re-execute requires Stop Loss to be enabled');
      }
      const type = leg.reEntry?.type;
      if (type !== 'SL_REENTRY' && type !== 'SL_RECOST' && type !== 'SL_REEXEC') {
        errors.push('Re-entry type must be SL_REENTRY, SL_RECOST, or SL_REEXEC');
      }
      if (reEntryCount > 2) {
        warnings.push('High re-entry count may lead to significant losses');
      }
      if (type === 'SL_RECOST' && reEntryCount > 1) {
        warnings.push('Re-Cost retries the same price — consider Re-Entry for adaptive timing');
      }
      if (type === 'SL_REEXEC' && reEntryCount > 3) {
        warnings.push('Re-Execute with high count can compound directional exposure — each retry is a fresh commitment at a worse price');
      }
    }

    // Re-execute validation (with defensive checks)
    if (leg.reExecute?.enabled) {
      const reExecuteCount = leg.reExecute?.count || 0;
      if (reExecuteCount <= 0 || reExecuteCount > 5) {
        errors.push('Re-execute count must be between 1 and 5');
      }
      if (!leg.targetProfit?.enabled) {
        warnings.push('Re-execute is typically used with Target Profit');
      }
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Validate strategy-level configuration
   */
  private validateStrategyConfig(strategyData: FrontendStrategyData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    const config = strategyData.config;

    // Time validation
    const entryTime = this.parseTime(config.entryTimeHour, config.entryTimeMinute);
    const exitTime = this.parseTime(config.exitTimeHour, config.exitTimeMinute);

    if (!this.isMarketHours(entryTime)) {
      warnings.push('Entry time is outside typical market hours (09:16-15:39 IST)');
    }

    if (!this.isMarketHours(exitTime)) {
      warnings.push('Exit time is outside typical market hours (09:16-15:39 IST)');
    }

    // POSITIONAL trading days validation takes priority
    if (config.tradingType === 'POSITIONAL') {
      const entryDays = config.entryTradingDaysBeforeExpiry;
      const exitDays = config.exitTradingDaysBeforeExpiry;

      if (entryDays === undefined || entryDays === null || exitDays === undefined || exitDays === null) {
        errors.push('Positional strategies require entry and exit trading days before expiry');
      }

      if (entryDays < 0) {
        errors.push('Entry trading days before expiry must be 0 or greater');
      }
      if (exitDays < 0) {
        errors.push('Exit trading days before expiry must be 0 or greater');
      }

      if (entryDays < exitDays) {
        errors.push('Entry trading days must be greater than or equal to exit trading days for positional strategies');
      }

      // Same-day POSITIONAL trades need intraday time validation
      if (entryDays === exitDays) {
        if (entryDays > 0) {
          warnings.push('Same day positional strategy: entry and exit on the same day before expiry');
        }

        // Apply intraday time validation ONLY for same-day positional trades
        if (entryTime >= exitTime) {
          errors.push('Entry time must be before exit time for same-day positional strategies');
        }
      }

      if (entryDays > 10) {
        warnings.push('Very early entry (>10 days before expiry) may have high time decay risk');
      }

      // Skip INTRADAY validation for POSITIONAL strategies
    } else if (config.tradingType === 'INTRADAY') {
      // INTRADAY time validation with proper BTST support
      const isNextDayExit = config.intradayExitMode === 'NEXT_DAY_BTST';

      if (!isNextDayExit && entryTime >= exitTime) {
        errors.push('Entry time must be before exit time');
      } else if (isNextDayExit && entryTime === exitTime) {
        // For NEXT_DAY_BTST, entry and exit can be at different times or same time (but different days)
        warnings.push('Entry and exit times are the same for next-day exit strategy');
      }

      // Duration warning - skip for NEXT_DAY_BTST as it spans across days
      if (!isNextDayExit) {
        if (exitTime - entryTime < 900000) { // 15 minutes in milliseconds
          warnings.push('Very short strategy duration may limit effectiveness');
        }
      } else {
        // For NEXT_DAY_BTST, calculate effective duration (entry today + exit tomorrow)
        const effectiveDuration = (24 * 3600000) - entryTime + exitTime; // Next day calculation
        if (effectiveDuration < 12 * 3600000) { // Less than 12 hours
          warnings.push('Short duration for next-day exit strategy');
        }
      }
    }

    // Range breakout validation (only for INTRADAY)
    // For NEXT_DAY_BTST, exit clock-time can be lower than range-breakout clock-time
    // because exit happens on the next day; therefore we must not compare against `exitTime`.
    const isNextDayExit = config.tradingType === 'INTRADAY' && config.intradayExitMode === 'NEXT_DAY_BTST';
    if (config.tradingType !== 'POSITIONAL' && config.rangeBreakout) {
      const rangeBreakoutTime = this.parseTime(config.rangeBreakoutTimeHour, config.rangeBreakoutTimeMinute);

      if (rangeBreakoutTime <= entryTime) {
        errors.push('Range breakout time must be after entry time');
      }

      // For SAME_DAY exit, exit time must be after range breakout time (since entry happens at breakout)
      if (!isNextDayExit && exitTime <= rangeBreakoutTime) {
        errors.push('Exit time must be after range breakout time for same-day strategies');
      }
    }

    // Risk management validation (with defensive checks)
    const targetProfitValue = config.targetProfit?.value || 0;
    const mtmStopLossValue = config.mtmStopLoss?.value || 0;

    // Per-field strategy-MTM validation (negative / over-cap are hard errors).
    // Combined-Premium % caps are leg-aware: a stop loss may exceed 100% only with
    // short exposure (any SELL leg); a target only with a long leg (any BUY leg).
    const { hasSellLeg, hasBuyLeg } = legExposure(strategyData.legs);
    const tpReason = getStrategyMtmInvalidReason(config.targetProfit, 'Strategy Target Profit', hasBuyLeg);
    if (tpReason) errors.push(tpReason);
    const slReason = getStrategyMtmInvalidReason(config.mtmStopLoss, 'Strategy Stop Loss', hasSellLeg);
    if (slReason) errors.push(slReason);

    // Large absolute TOTAL_MTM thresholds are unusual — warn (don't block).
    if (config.targetProfit?.type === 'TOTAL_MTM' && targetProfitValue > 1_000_000) {
      warnings.push('Strategy Target Profit is unusually large (> ₹10,00,000) — confirm the value');
    }
    if (config.mtmStopLoss?.type === 'TOTAL_MTM' && mtmStopLossValue > 1_000_000) {
      warnings.push('Strategy Stop Loss is unusually large (> ₹10,00,000) — confirm the value');
    }

    if (targetProfitValue > 0 && mtmStopLossValue > 0) {
      const isTPPercent = config.targetProfit?.type === 'COMBINED_PREMIUM_PERCENT';
      const isSLPercent = config.mtmStopLoss?.type === 'COMBINED_PREMIUM_PERCENT';

      if (isTPPercent && isSLPercent) {
        if (targetProfitValue <= mtmStopLossValue) {
          warnings.push('Target profit should typically be higher than stop loss for better risk-reward ratio');
        }
      }
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Advanced strategy validation rules
   */
  private validateAdvancedRules(strategyData: FrontendStrategyData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Multi-leg strategy validation
    if (strategyData.legs.length > 1) {
      const multiLegValidation = this.validateMultiLegStrategy(strategyData);
      errors.push(...multiLegValidation.errors);
      warnings.push(...multiLegValidation.warnings);
    }

    // Index-specific validation
    const indexValidation = this.validateIndexSpecificRules(strategyData);
    errors.push(...indexValidation.errors);
    warnings.push(...indexValidation.warnings);

    // Capital requirements estimation
    const capitalValidation = this.validateCapitalRequirements(strategyData);
    warnings.push(...capitalValidation.warnings);

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Validate multi-leg strategy configurations
   */
  private validateMultiLegStrategy(strategyData: FrontendStrategyData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    const legs = strategyData.legs;
    
    // Check for balanced strategy (equal buy/sell legs)
    const buyLegs = legs.filter(leg => leg.actionType === 'BUY').length;
    const sellLegs = legs.filter(leg => leg.actionType === 'SELL').length;
    
    if (Math.abs(buyLegs - sellLegs) > 2) {
      warnings.push('Highly unbalanced buy/sell ratio may indicate unusual risk profile');
    }

    // Expiry type is now strategy-level only, so no leg-level consistency check needed

    // Detect common strategy patterns and validate
    const strategyPattern = this.detectStrategyPattern(legs);
    if (strategyPattern) {
      const patternValidation = this.validateStrategyPattern(strategyPattern, legs);
      errors.push(...patternValidation.errors);
      warnings.push(...patternValidation.warnings);
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Validate index-specific rules
   */
  private validateIndexSpecificRules(strategyData: FrontendStrategyData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    const indexLotSizes = {
      'NIFTY': 25,
      'BANKNIFTY': 15,
      'FINNIFTY': 25,
      'MIDCPNIFTY': 75,
      'SENSEX': 10,
      'BANKEX': 15,
    };

    const lotSize = indexLotSizes[strategyData.index as keyof typeof indexLotSizes];
    
    if (lotSize) {
      const totalLots = strategyData.legs.reduce((sum, leg) => sum + leg.totalLots, 0);
      const totalQuantity = totalLots * lotSize;
      
      if (totalQuantity > 1800) { // NSE position limit threshold
        warnings.push(`High total quantity (${totalQuantity}) may approach position limits`);
      }

      // Index-specific warnings
      if (strategyData.index === 'BANKNIFTY' && totalLots > 50) {
        warnings.push('High BANKNIFTY lot count requires significant margin');
      }
      
      if (strategyData.index === 'MIDCPNIFTY' && totalLots > 10) {
        warnings.push('MIDCPNIFTY has large lot sizes (75) - verify capital requirements');
      }
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Estimate capital requirements and validate
   */
  private validateCapitalRequirements(strategyData: FrontendStrategyData): ValidationResult {
    const warnings: string[] = [];

    const totalLots = strategyData.legs.reduce((sum, leg) => sum + leg.totalLots, 0);
    const netSellLegs = strategyData.legs.filter(leg => leg.actionType === 'SELL').length;

    if (totalLots > 20) {
      warnings.push('High lot count strategy may require substantial margin');
    }

    if (netSellLegs > 0) {
      warnings.push('Strategy involves option selling - ensure adequate margin availability');
    }

    if (strategyData.legs.length > 4 && totalLots > 10) {
      warnings.push('Complex multi-leg strategy with high lots may have execution slippage');
    }

    return { isValid: true, errors: [], warnings };
  }

  /**
   * Detect common options strategy patterns
   */
  private detectStrategyPattern(legs: Leg[]): string | null {
    if (legs.length === 2) {
      const ceLegs = legs.filter(leg => leg.optionType === 'CE');
      const peLegs = legs.filter(leg => leg.optionType === 'PE');
      
      if (ceLegs.length === 1 && peLegs.length === 1) {
        const ceLeg = ceLegs[0];
        const peLeg = peLegs[0];
        
        if (ceLeg.strikePrice === peLeg.strikePrice) {
          if (ceLeg.actionType === 'BUY' && peLeg.actionType === 'BUY') {
            return 'LONG_STRADDLE';
          } else if (ceLeg.actionType === 'SELL' && peLeg.actionType === 'SELL') {
            return 'SHORT_STRADDLE';
          }
        }
      }
    } else if (legs.length === 4) {
      const sellLegs = legs.filter(leg => leg.actionType === 'SELL');
      const buyLegs = legs.filter(leg => leg.actionType === 'BUY');
      
      if (sellLegs.length === 2 && buyLegs.length === 2) {
        return 'IRON_CONDOR';
      }
    }

    return null;
  }

  /**
   * Validate specific strategy patterns
   */
  private validateStrategyPattern(pattern: string, legs: Leg[]): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    switch (pattern) {
      case 'LONG_STRADDLE':
        warnings.push('Long Straddle requires significant price movement to be profitable');
        break;
      case 'SHORT_STRADDLE':
        warnings.push('Short Straddle has unlimited risk - ensure proper risk management');
        break;
      case 'IRON_CONDOR':
        warnings.push('Iron Condor profits from low volatility - monitor implied volatility');
        break;
    }

    return { isValid: errors.length === 0, errors, warnings };
  }

  /**
   * Utility methods
   */
  private isValidATMPointStrike(strike: string): boolean {
    return /^(ATM|ITM\d+|OTM\d+)$/.test(strike);
  }

  private isValidATMPercentStrike(strike: string): boolean {
    return /^(ATM|ATM[+-]\d+(\.\d+)?%)$/.test(strike);
  }

  private parseTime(hour: string, minute: string): number {
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    return h * 3600000 + m * 60000; // Convert to milliseconds
  }

  private isMarketHours(timeMs: number): boolean {
    const marketOpen =
      MARKET_HOURS.SESSION_START_HOUR * 3600000 +
      MARKET_HOURS.SESSION_START_MINUTE * 60000; // 09:16
    const marketClose =
      MARKET_HOURS.SESSION_LAST_HOUR * 3600000 +
      MARKET_HOURS.SESSION_LAST_MINUTE * 60000; // 15:39
    return timeMs >= marketOpen && timeMs <= marketClose;
  }
}

const strategyValidationService = new StrategyValidationService();
export default strategyValidationService;
export { StrategyValidationService };
export type { ValidationResult, LegValidationResult };